import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatErr = (d) => {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || "").filter(Boolean).join(" · ");
  return String(d);
};

export default function Redeem() {
  const { user, loading, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [unlocked, setUnlocked] = useState(null);

  if (loading) return <div className="py-24 text-center text-[#6B5E62]">Loading…</div>;
  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 py-14 bg-[#FAF4F5]">
        <div className="max-w-md text-center bg-white border border-[#E8DCDD] rounded-sm p-8">
          <KeyRound className="w-8 h-8 mx-auto text-[#8C4A57] mb-3"/>
          <h1 className="font-serif text-2xl mb-2">Sign in to redeem</h1>
          <p className="text-sm text-[#6B5E62] mb-6">You need a student account so we can attach the unlocked content to you.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/login?next=/redeem" className="border border-[#8C4A57] text-[#8C4A57] hover:bg-[#8C4A57] hover:text-white px-5 py-2 rounded-sm text-[11px] tracking-widest uppercase font-semibold transition">Sign in</Link>
            <Link to="/register" className="bg-[#8C4A57] text-white px-5 py-2 rounded-sm text-[11px] tracking-widest uppercase font-semibold hover:bg-[#6f3945] transition">Create account</Link>
          </div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/redeem`, { code: code.trim().toUpperCase() },
        { headers: authHeaders() });
      setUnlocked(r.data);
      toast.success(`Unlocked ${r.data.unlocked.length} item(s)`);
      setCode("");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[60vh] py-14 px-6 bg-[#FAF4F5]" data-testid="redeem-page">
      <div className="max-w-md mx-auto bg-white border border-[#E8DCDD] rounded-sm p-8">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Access code</div>
        <h1 className="font-serif text-3xl text-[#2D2628] mb-1">Redeem</h1>
        <p className="text-sm text-[#6B5E62] mb-6">Enter your access code to unlock the courses or bundles it grants — the content will appear in your library instantly.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Access code</label>
            <input type="text" required autoFocus autoCapitalize="characters" spellCheck="false"
              value={code} onChange={e => setCode(e.target.value.toUpperCase())}
              data-testid="redeem-code-input" placeholder="EXAMPLE-1234"
              className="w-full border border-[#E8DCDD] rounded-sm px-3 py-3 text-base font-mono uppercase tracking-widest text-center mt-1"/>
          </div>
          <button type="submit" disabled={busy || !code.trim()} data-testid="redeem-submit"
            className="w-full bg-[#8C4A57] text-white text-[12px] tracking-[0.14em] uppercase font-semibold py-3 rounded-sm hover:bg-[#6f3945] transition disabled:opacity-60">
            {busy ? "Redeeming…" : "Redeem code"}
          </button>
        </form>
        {unlocked && (
          <div data-testid="redeem-success" className="mt-6 border border-[#B8D8B0] bg-[#F0F7EE] rounded-sm p-5">
            <div className="flex items-center gap-2 text-[#3C7A2B] font-semibold text-sm mb-2">
              <CheckCircle2 className="w-4 h-4"/> Unlocked {unlocked.unlocked.length} item{unlocked.unlocked.length > 1 ? "s" : ""}
            </div>
            <ul className="text-[13px] text-[#2D2628] space-y-1">
              {unlocked.products.map((t, i) => <li key={i}>· {t}</li>)}
            </ul>
            <button onClick={() => navigate("/dashboard")}
              className="mt-4 text-[11px] tracking-widest uppercase text-[#8C4A57] font-semibold hover:underline">
              Go to My Library →
            </button>
          </div>
        )}
        <p className="text-[11px] text-[#6B5E62] mt-6 text-center italic">
          Codes are one-time per account and case-insensitive.
        </p>
      </div>
    </div>
  );
}
